import { Gender, GenderLabel } from "@/constants";
import { useDisclosure } from "@/hooks";
import { cn } from "@/libs/cn";
import { convertEnglishToBurmeseNumerals } from "@/libs/utils";
import { Button, Divider, Modal } from "antd";

const TypeOrder = Object.values(Gender);

const InitialYogiCountByTypes = [
	{
		type: Gender.MONK,
		count: 0,
	},
	{
		type: Gender.NUN,
		count: 0,
	},
	{
		type: Gender.MALE,
		count: 0,
	},
	{
		type: Gender.FEMALE,
		count: 0,
	},
	{
		type: Gender.UNKNOWN,
		count: 0,
	},
];

export function RegistrantGenderCountModal(props: MeditationRetreat) {
	const { yogiCount, maxYogiLimit, yogiCountByTypes } = props;

	const [opened, { toggle }] = useDisclosure(false);

	const availableYogiCount = yogiCountByTypes
		? maxYogiLimit -
			yogiCountByTypes?.reduce((acc, item) => acc + item.count, 0)
		: maxYogiLimit -
				InitialYogiCountByTypes?.reduce(
					(acc, item) => acc + item.count,
					0,
				) || 0;

	const orderedYogiTypes = yogiCountByTypes?.slice().sort((a, b) => {
		const indexA = TypeOrder.indexOf(a.type as Gender);
		const indexB = TypeOrder.indexOf(b.type as Gender);
		return indexA - indexB;
	});

	const renderData = orderedYogiTypes
		? orderedYogiTypes
		: InitialYogiCountByTypes;

	return (
		<>
			<Button
				type="text"
				size="small"
				onClick={toggle}
				className="!font-poppins"
				danger={yogiCount === maxYogiLimit}
			>
				<span
					className={cn({
						"text-success": yogiCount < maxYogiLimit,
					})}
				>
					{yogiCount}
				</span>
				/ {maxYogiLimit}
			</Button>
			<Modal
				open={opened}
				onCancel={toggle}
				onOk={toggle}
				title="ဦးရေ အမျိုးအစားများ"
				footer={null}
				centered
				classNames={{
					content: "!rounded-sm",
				}}
			>
				<div className="grid grid-cols-10 gap-4 p-4 bg-neutral-50 rounded-sm justify-items-center ">
					{renderData.map((item) =>
						Array.from({ length: item.count })
							.fill(null)
							.map((_, index) => (
								<ColorBall
									key={`${item.type}-${index}`}
									color={getGenderColor(item.type as Gender)}
								/>
							)),
					)}

					{availableYogiCount > 0 &&
						Array.from({ length: availableYogiCount })
							.fill(null)
							.map((_, index) => (
								<ColorBall key={`available-${index}`} />
							))}
				</div>
				<Divider />
				<div className="grid grid-cols-4 gap-4 justify-items-center ">
					{renderData?.map((item) => (
						<div
							key={item.type}
							className="space-y-3 flex flex-col items-center justify-center "
						>
							<ColorBall
								color={getGenderColor(item.type as Gender)}
								size={20}
							/>

							<article className="space-y-2 text-center">
								<h3 className="font-semibold">
									{GenderLabel[item.type as Gender]}
								</h3>
								<p>
									(
									{convertEnglishToBurmeseNumerals(
										item.count.toString(),
									)}
									)
								</p>
							</article>
						</div>
					))}
				</div>
				<div className="mt-8 space-y-3">
					<p className="text-center">လက်ခံရန် ကျန်ရှိသည့်ဦးရေ</p>
					<p className="text-center p-2 border rounded-xs bg-neutral-100 ">
						{convertEnglishToBurmeseNumerals(
							(availableYogiCount ?? 0).toString(),
						)}
					</p>
				</div>
			</Modal>
		</>
	);
}

function getGenderColor(type: Gender) {
	switch (type) {
		case Gender.MALE:
			return "bg-blue-500";
		case Gender.FEMALE:
			return "bg-yellow-500";
		case Gender.MONK:
			return "bg-red-500";
		case Gender.NUN:
			return "bg-pink-500";
		default:
			return "bg-gray-500";
	}
}

type ColorBallProps = {
	size?: number;
	color?: string;
} & React.HTMLAttributes<HTMLSpanElement>;

const ColorBall = ({
	size = 16,
	color = "bg-gray-300",
	...props
}: ColorBallProps) => {
	return (
		<span
			className={cn("inline-block rounded-full", color)}
			style={{ width: size, height: size }}
			{...props}
		/>
	);
};
